import { formatExhibitTokenBare } from "../../helpers/exhibitNumbering";
import type { ExhibitStyle } from "../../types/exhibit_formats";
import type { ExhibitScheme, FsNode } from "./types";
import { isFolder, isIncluded, isNumbered } from "./types";

/**
 * The TypeScript mirror of `caseblink/services/exhibit_label_service.py`.
 *
 * It exists so a drag renumbers the whole tree in the same frame rather than
 * after a round trip. The two implementations have to agree, so the rules are
 * kept deliberately mechanical: depth-first pre-order over `sort_key`, counters
 * per parent, and no state carried between nodes.
 */

const LABEL_SEPARATOR = ".";

export type ExhibitLabels = Record<string, string | null>;

const groupChildren = (nodes: FsNode[]): Map<string | null, FsNode[]> => {
  const grouped = new Map<string | null, FsNode[]>();
  nodes.forEach((node) => {
    if (node.is_deleted) return;
    const key = node.parent_id ?? null;
    const siblings = grouped.get(key);
    if (siblings) {
      siblings.push(node);
    } else {
      grouped.set(key, [node]);
    }
  });
  grouped.forEach((siblings) =>
    siblings.sort((a, b) =>
      a.sort_key < b.sort_key ? -1 : a.sort_key > b.sort_key ? 1 : 0
    )
  );
  return grouped;
};

const isLabelable = (node: FsNode) => isIncluded(node) && isNumbered(node);

/** A folder kept in the packet but excluded from numbering. */
export const isHeading = (node: FsNode) =>
  isFolder(node) && isIncluded(node) && !isNumbered(node);

/**
 * The style for one level, reusing the last configured one past the end.
 * Mirrors `ExhibitScheme.style_for_depth` in the backend.
 */
export const styleForDepth = (
  scheme: ExhibitScheme,
  depth: number
): ExhibitStyle => {
  if (!scheme.levels || scheme.levels.length === 0) return scheme.default_style;
  return scheme.levels[Math.min(depth, scheme.levels.length - 1)];
};

/**
 * The first labeled style at `depth` or deeper: what a document sitting at an
 * unlabeled (`NONE`) level numbers with. Mirrors `labeled_style_from`.
 */
export const labeledStyleFrom = (
  scheme: ExhibitScheme,
  depth: number
): ExhibitStyle => {
  const last = Math.max(depth, scheme.levels?.length ?? 0);
  for (let index = depth; index <= last; index += 1) {
    const style = styleForDepth(scheme, index);
    if (style.token !== "NONE") return style;
  }
  return scheme.default_style;
};

/** Where a whole label takes its prefix and ending: the first labeled level. */
export const labelStyle = (scheme: ExhibitScheme): ExhibitStyle =>
  labeledStyleFrom(scheme, 0);

/**
 * A scheme with the style at `level` replaced. Style is a property of the
 * level, so this is how a column header changes numbering for every folder at
 * its depth.
 *
 * Levels past the end reuse the last one, so setting a level that is currently
 * the last would also reformat everything deeper. To keep each level
 * independent, the gaps up to and one past `level` are materialised from the
 * styles they resolve to today before the target is overwritten.
 */
export const setLevelStyle = (
  scheme: ExhibitScheme,
  level: number,
  style: ExhibitStyle
): ExhibitScheme => {
  const length = Math.max(scheme.levels.length, level + 2);
  const levels = Array.from({ length }, (_, index) =>
    index === level ? style : styleForDepth(scheme, index)
  );
  return { ...scheme, levels };
};

/**
 * The nodes competing for one counter, in order. A heading folder is replaced
 * in place by its own children, recursively, so everything under it numbers as
 * if the folder were not there. Excluded nodes drop out with their subtrees.
 */
const numberingSiblings = (
  parentId: string | null,
  grouped: Map<string | null, FsNode[]>
): FsNode[] =>
  (grouped.get(parentId) ?? []).flatMap((node) => {
    if (!isIncluded(node)) return [];
    if (isHeading(node)) return numberingSiblings(node.id, grouped);
    return [node];
  });

/**
 * How one Miller column is numbered: its numbering level, the style that level
 * takes, and whether the folder it lists is a heading.
 *
 * A column's position on screen is not its numbering level, because a heading
 * folder adds no level — its children number as its own siblings do. So the
 * level is the count of non-heading ancestors, and every column at that level
 * shares one style regardless of which folder it lists.
 */
export const columnNumbering = (
  parentId: string | null,
  nodesById: Map<string, FsNode>,
  scheme: ExhibitScheme
): { level: number; style: ExhibitStyle; isHeadingColumn: boolean } => {
  const ancestors: FsNode[] = [];
  let cursor = parentId ? nodesById.get(parentId) : undefined;
  while (cursor) {
    ancestors.push(cursor);
    cursor = cursor.parent_id ? nodesById.get(cursor.parent_id) : undefined;
  }

  const level = ancestors.filter((node) => !isHeading(node)).length;
  return {
    level,
    style: styleForDepth(scheme, level),
    isHeadingColumn: ancestors.length > 0 && isHeading(ancestors[0])
  };
};

const hasNumberedFile = (
  folderId: string,
  grouped: Map<string | null, FsNode[]>
): boolean =>
  // Looks through headings, so a folder holding only a heading full of real
  // documents still counts as numbered.
  numberingSiblings(folderId, grouped).some((child) => {
    if (!isLabelable(child)) return false;
    return isFolder(child) ? hasNumberedFile(child.id, grouped) : true;
  });

/**
 * Returns `node_id -> label` for every node, with `null` where a node carries no
 * number. Every node is present so the result can be written straight over the
 * previous labels.
 *
 * `excluded` prunes a node and its subtree. `unnumbered` keeps a node in the
 * packet but consumes no counter, and on a folder also makes it transparent to
 * numbering: it contributes no label segment, and its children take their
 * numbers from the enclosing level's counter as though they sat there directly.
 * That is what makes an unnumbered folder usable as a heading.
 *
 * Style is a property of the level: every folder at a given depth is numbered
 * with `styleForDepth(scheme, depth)`. At an unlabeled (`NONE`) level every
 * folder is a heading: no label, and their children share one counter, so
 * numbers run on across folders instead of repeating in each. A document at
 * that level numbers in the same run with the first labeled style below.
 */
export const computeExhibitLabels = (
  nodes: FsNode[],
  scheme: ExhibitScheme
): ExhibitLabels => {
  const labels: ExhibitLabels = {};
  nodes.forEach((node) => {
    labels[node.id] = null;
  });
  const grouped = groupChildren(nodes);

  const walk = (
    parentId: string | null,
    parentLabel: string | null,
    depth: number,
    run: { count: number } = { count: 0 }
  ) => {
    const unlabeled = styleForDepth(scheme, depth).token === "NONE";
    const style = labeledStyleFrom(scheme, depth);

    numberingSiblings(parentId, grouped).forEach((node) => {
      if (!isLabelable(node)) return;
      // An empty folder would otherwise burn a number that appears nowhere.
      if (isFolder(node) && !hasNumberedFile(node.id, grouped)) return;

      if (unlabeled && isFolder(node)) {
        walk(node.id, parentLabel, depth + 1, run);
        return;
      }

      const token = formatExhibitTokenBare(run.count + scheme.start_at, style);
      const label = parentLabel
        ? `${parentLabel}${LABEL_SEPARATOR}${token}`
        : token;
      labels[node.id] = label;
      run.count += 1;

      if (isFolder(node)) walk(node.id, label, depth + 1);
    });
  };
  walk(null, null, 0);

  return labels;
};

/**
 * Renders a stored label. A prefix replaces the punctuation rather than sitting
 * alongside it, matching `formatExhibitToken`.
 */
export const formatExhibitLabel = (
  label: string | null | undefined,
  scheme: ExhibitScheme
): string => {
  if (!label) return "";
  const style = labelStyle(scheme);
  if (style.prefix) return `${style.prefix} ${label}`;
  return `${label}${style.punctuation}`;
};

/**
 * The nodes that belong in the packet, in packet order. Shares the traversal
 * with label computation so the exhibit list and the merged PDF cannot disagree.
 */
export const packetOrderedNodes = (nodes: FsNode[]): FsNode[] => {
  const grouped = groupChildren(nodes);
  const ordered: FsNode[] = [];

  const walk = (parentId: string | null) => {
    (grouped.get(parentId) ?? []).forEach((node) => {
      if (!isIncluded(node)) return;
      ordered.push(node);
      if (isFolder(node)) walk(node.id);
    });
  };
  walk(null);

  return ordered;
};
